import { geometryMeasures, type GeometryShape } from './mathModels';

export const geometryNumber = (value: number) =>
  value.toLocaleString('en-AU', { maximumSignificantDigits: 6, useGrouping: false });
export type GeometryStep = { title: string; formula: string; explanation: string; focus: string };
export function geometryExplorer(shape: GeometryShape, draft: { first: string; second: string }) {
  const round = ['circle', 'cylinder', 'cone', 'sphere'].includes(shape);
  const one = shape === 'sphere' || shape === 'circle';
  const read = (text: string, name: string) => {
    if (!text.trim()) throw new Error(`Enter ${name} to draw your shape.`);
    const value = Number(text);
    if (!Number.isFinite(value) || value < 0.1 || value > 1000)
      throw new Error(`${name} must be between 0.1 and 1000 cm. Your input has been kept.`);
    return value;
  };
  const a = read(draft.first, round ? 'Radius' : 'Base length');
  const b = one ? a : read(draft.second, 'Perpendicular height');
  const n = geometryNumber;
  const steps: GeometryStep[] = [];
  const add = (title: string, formula: string, explanation: string, focus: string) =>
    steps.push({ title, formula, explanation, focus });
  // Substitutions use the actual input numbers. Only displayed answers are rounded.
  if (shape === 'rectangle') {
    add(
      'Count square units',
      `A = b × h = ${a} × ${b} = ${n(a * b)} cm²`,
      'Each row has b unit squares; there are h rows.',
      'base',
    );
    add(
      'Add the four edges',
      `P = 2(b + h) = 2(${a} + ${b}) = ${n(2 * (a + b))} cm`,
      'Perimeter is boundary length; area measures the space inside.',
      'surface',
    );
  } else if (shape === 'triangle') {
    add(
      'Identify the perpendicular height',
      `b = ${a} cm; h = ${b} cm`,
      'The dashed height meets the base at 90°. A sloping side is not the height.',
      'height',
    );
    add(
      'Build a parallelogram',
      `b × h = ${a} × ${b} = ${n(a * b)} cm²`,
      'Two congruent copies form a parallelogram with the same base and perpendicular height.',
      'base',
    );
    add(
      'Take half',
      `A = ½bh = ½ × ${a} × ${b} = ${n((a * b) / 2)} cm²`,
      'One triangle occupies half the parallelogram. Base and height alone do not determine the perimeter.',
      'surface',
    );
  } else if (shape === 'circle') {
    add(
      'Radius and diameter',
      `d = 2r = 2 × ${a} = ${n(2 * a)} cm`,
      'Radius goes from centre to edge; diameter crosses the whole circle.',
      'radius',
    );
    add(
      'Area inside the circle',
      `A = πr² = π × ${a}² ≈ ${n(Math.PI * a * a)} cm²`,
      'Square the radius before multiplying by π. Area uses square units.',
      'surface',
    );
    add(
      'Length around the circle',
      `C = 2πr = 2 × π × ${a} ≈ ${n(2 * Math.PI * a)} cm`,
      'Circumference measures the boundary, so its unit is cm.',
      'radius',
    );
  } else if (shape === 'sphere') {
    add(
      'Read the radius',
      `r = ${a} cm; d = 2r = ${n(2 * a)} cm`,
      'A sphere needs only its radius. Its total height is the diameter, not the radius.',
      'radius',
    );
    add(
      'Cover the curved surface',
      `S = 4πr² = 4 × π × ${a}² ≈ ${n(4 * Math.PI * a * a)} cm²`,
      'The sphere surface-area formula gives the area of four circles of radius r. There is no separate flat base.',
      'surface',
    );
    add(
      'Measure the space inside',
      `V = ⁴⁄₃πr³ = 4/3 × π × ${a}³ ≈ ${n((4 / 3) * Math.PI * a ** 3)} cm³`,
      'Cube the radius, multiply by π, then by 4/3. This is the sphere volume formula; this substitution is not a proof of the formula.',
      'volume',
    );
    add(
      'Check with an enclosing cylinder',
      `V(cylinder) = π × ${a}² × ${2 * a} ≈ ${n(2 * Math.PI * a ** 3)} cm³`,
      'A cylinder of radius r and height 2r encloses this sphere. The sphere occupies 2/3 of its volume. Doubling r multiplies surface area by 4 and volume by 8.',
      'height',
    );
  } else {
    const base = (shape === 'prism' ? 1 : Math.PI) * a * a;
    add(
      'Find the base area',
      shape === 'prism'
        ? `B = b² = ${a}² = ${n(base)} cm²`
        : `B = πr² = π × ${a}² ≈ ${n(base)} cm²`,
      shape === 'prism'
        ? 'The base is a square, so both base edges have the same length.'
        : 'The base is a circle. Use radius, not diameter.',
      'base',
    );
    if (shape === 'cone') {
      const slant = Math.hypot(a, b);
      add(
        'Find the slant height',
        `l = √(r² + h²) = √(${a}² + ${b}²) ≈ ${n(slant)} cm`,
        'Radius and perpendicular height form a right triangle. The slant height l is its hypotenuse; it is not h.',
        'slant',
      );
      add(
        'Find the volume',
        `V = ⅓Bh = ⅓ × π × ${a}² × ${b} ≈ ${n((base * b) / 3)} cm³`,
        'A cone holds one third as much as a cylinder with the same radius and perpendicular height.',
        'volume',
      );
      add(
        'Unwrap the curved surface',
        `L = πrl = π × ${a} × √(${a}² + ${b}²) ≈ ${n(Math.PI * a * slant)} cm²`,
        'The curved surface unfolds to a sector of radius l. Its arc length is 2πr, so sector area is ½ × l × 2πr = πrl. Use the unrounded slant height.',
        'slant',
      );
      add(
        'Add the base and curved area',
        `S = πr² + πrl ≈ ${n(base)} + ${n(Math.PI * a * slant)} ≈ ${n(base + Math.PI * a * slant)} cm²`,
        'Total surface area includes one circular base and the curved surface. Volume uses h; curved surface area uses l.',
        'surface',
      );
    } else {
      add(
        'Stack the base through the height',
        `V = Bh = ${shape === 'prism' ? `${a}²` : `π × ${a}²`} × ${b} ${shape === 'prism' ? '=' : '≈'} ${n(base * b)} cm³`,
        'Volume counts how much three-dimensional space is inside. Use perpendicular height.',
        'volume',
      );
      add(
        'Find the side area',
        shape === 'prism'
          ? `L = 4bh = 4 × ${a} × ${b} = ${n(4 * a * b)} cm²`
          : `L = 2πrh = 2 × π × ${a} × ${b} ≈ ${n(2 * Math.PI * a * b)} cm²`,
        shape === 'prism'
          ? 'There are four rectangles, each with area base side × height.'
          : 'The curved wall unwraps to a rectangle: width = circumference 2πr and height = h.',
        'height',
      );
      add(
        'Include both ends',
        `S = 2B + L ${shape === 'prism' ? '=' : '≈'} ${n(2 * base + (shape === 'prism' ? 4 : 2 * Math.PI) * a * b)} cm²`,
        'Total surface area includes the side area and two equal bases. These are closed solids.',
        'surface',
      );
    }
  }
  const measures = geometryMeasures(shape, a, b);
  const solid = measures.volume !== null;
  return {
    shape,
    a,
    b,
    steps,
    measures,
    solid,
    primary: solid ? measures.volume! : measures.area!,
    exact:
      shape === 'sphere'
        ? `V = (4 × ${a}³ / 3)π cm³; S = (4 × ${a}²)π cm²`
        : shape === 'cone'
          ? `V = (${a}² × ${b} / 3)π cm³; S = (${a}² + ${a}√(${a}² + ${b}²))π cm²`
          : shape === 'circle'
            ? `A = (${a}²)π cm²; C = (2 × ${a})π cm`
            : shape === 'cylinder'
              ? `V = (${a}² × ${b})π cm³; S = 2 × ${a}(${a} + ${b})π cm²`
              : null,
  };
}
